import React from 'react'

import { IconPencilFilled, IconTrashFilled, IconWorldCog, IconWorldStar, IconX } from '@tabler/icons-react'
import alertify from 'alertifyjs'
import cloneDeep from 'lodash.clonedeep'
import { actions } from '#/actions'
import assetStore, { type AssetStoreData } from '#/assetStore'
import bem from '#/bem'
import Button from '#/components/common/button'
import InlineMessage from '#/components/common/inlineMessage'
import LoadingSpinner from '#/components/common/loadingSpinner'
import { LockingRestrictionName } from '#/components/locking/lockingConstants'
import { hasAssetRestriction } from '#/components/locking/lockingUtils'
import LanguageForm from '#/components/modalForms/languageForm'
import { MODAL_TYPES } from '#/constants'
import type { AssetContent, AssetResponse, SureveyRowOrChoiceTranslatableProp, SurveyRow } from '#/dataInterface'
import envStore from '#/envStore'
import pageState from '#/pageState.store'
import { type LangObject, escapeHtml, getLangString, notify } from '#/utils'
import ActionIcon from '../common/ActionIcon'

const LANGUAGE_SUPPORT_URL = 'language_dashboard.html'

interface TranslationSettingsProps {
  asset: AssetResponse
}

interface TranslationSettingsState {
  asset: AssetResponse
  translations: AssetContent['translations']
  showAddLanguageForm: boolean
  isUpdatingAsset: boolean
  renameLanguageIndex: number | -1
}

export class TranslationSettings extends React.Component<TranslationSettingsProps, TranslationSettingsState> {
  private unlisteners: Function[] = []

  constructor(props: TranslationSettingsProps) {
    super(props)

    this.state = {
      asset: props.asset,
      translations: props.asset.content?.translations,
      showAddLanguageForm: false,
      isUpdatingAsset: false,
      renameLanguageIndex: -1,
    }
  }

  componentDidMount() {
    this.unlisteners.push(assetStore.listen(this.onAssetsChange.bind(this), this))
  }

  componentWillUnmount() {
    this.unlisteners.forEach((clb) => {
      clb()
    })
  }

  onAssetChange(asset: AssetResponse) {
    this.setState({
      asset: asset,
      translations: asset.content?.translations || [],
      showAddLanguageForm: false,
      isUpdatingAsset: false,
      renameLanguageIndex: -1,
    })

    pageState.showModal({
      type: MODAL_TYPES.FORM_LANGUAGES,
      asset: asset,
    })
  }

  onAssetsChange(assetsList: AssetStoreData) {
    if (assetsList[this.state.asset.uid]) {
      this.onAssetChange(assetsList[this.state.asset.uid])
    }
  }

  showAddLanguageForm() {
    this.setState({ showAddLanguageForm: true })
  }

  hideAddLanguageForm() {
    this.setState({ showAddLanguageForm: false })
  }

  toggleRenameLanguageForm(index: number) {
    if (this.state.renameLanguageIndex === index) {
      this.setState({ renameLanguageIndex: -1 })
    } else {
      this.setState({ renameLanguageIndex: index })
    }
  }

  launchTranslationTableModal(index: number) {
    // This most probably is a `string`
    const langString = this.state.translations?.[index]

    pageState.switchModal({
      type: MODAL_TYPES.FORM_TRANSLATIONS_TABLE,
      asset: this.state.asset,
      langString: langString,
      langIndex: index,
    })
  }

  onLanguageChange(lang: LangObject, index: number) {
    let content = this.state.asset.content
    const langString = getLangString(lang)

    if (content?.translations) {
      if (index > -1) {
        content.translations[index] = langString || null
      } else {
        content.translations.push(langString || null)
        content = this.prepareTranslations(content)
      }

      if (index === 0) {
        if (content?.settings) {
          content.settings.default_language = langString
        }
      }

      this.updateAsset(content)
    }
  }

  // Check if the default `null` language has been replaced yet
  canAddLanguages() {
    return !(this.state.translations?.length === 1 && this.state.translations[0] === null)
  }

  // `language_edit` restriction implies canAddLanguages but restricts it
  canEditLanguages() {
    return !this.isEditingLanguagesLocked() && this.canAddLanguages()
  }

  getAllLanguages() {
    return this.state.translations
  }

  deleteLanguage(index: number) {
    const content = this.deleteTranslations(index)
    if (content) {
      content?.translations?.splice(index, 1)
      const dialog = alertify.dialog('confirm')
      const opts = {
        title: t('Delete language?'),
        message: t('Are you sure you want to delete this language? This action is not reversible.'),
        labels: { ok: t('Delete'), cancel: t('Cancel') },
        onok: () => {
          this.updateAsset(content)
          dialog.destroy()
        },
        oncancel: dialog.destroy,
      }
      dialog.set(opts).show()
    } else {
      notify(t('Translation index mismatch. Cannot delete language.'), 'error')
    }
  }

  prepareTranslations(contentOriginal: AssetContent) {
    // Clone it to make sure we don't have unpredicted behaviors
    const content = cloneDeep(contentOriginal)

    // Ensure all parts are there, if not let's just return what we got
    if (!content.translated || !content.translations) {
      return content
    }

    if (content.survey) {
      // append null values to translations for each survey row
      for (let i = 0, len = content.survey.length; i < len; i++) {
        const row = content.survey[i]
        for (let j = 0, len2 = content.translated.length; j < len2; j++) {
          const propertyName = content.translated[j]
          const rowProperty = row[propertyName as keyof SurveyRow]
          if (Array.isArray(rowProperty) && rowProperty.length < content.translations.length) {
            // Casting this to satisfy TS. Possible there is better way to tell TS we are not dealing with `string[]` here
            ;(rowProperty as SureveyRowOrChoiceTranslatableProp).push(null)
          }
        }
      }
    }

    // append null values to translations for choices
    if (content.choices && content.choices.length) {
      for (let i = 0, len = content.choices.length; i < len; i++) {
        const choice = content.choices[i]
        if (Array.isArray(choice.label) && choice.label.length < content.translations.length) {
          choice.label.push(null)
        }
      }
    }
    return content
  }

  deleteTranslations(langIndex: number) {
    const content = cloneDeep(this.state.asset.content)

    // Ensure all parts are there, if not let's just return what we got
    if (!content?.translated || !content.translations) {
      return content
    }

    if (content.survey) {
      for (let i = 0, len = content.survey.length; i < len; i++) {
        const row = content.survey[i]
        for (let j = 0, len2 = content.translated.length; j < len2; j++) {
          const propertyName = content.translated[j]
          const rowProperty = row[propertyName as keyof SurveyRow]
          if (Array.isArray(rowProperty)) {
            if (rowProperty.length === content.translations.length) {
              rowProperty.splice(langIndex, 1)
            } else {
              console.error('Translations index mismatch')
              return false
            }
          }
        }
      }
    }

    if (content.choices && content.choices.length) {
      for (let i = 0, len = content.choices.length; i < len; i++) {
        const choice = content.choices[i]
        if (choice.label) {
          if (choice.label.length === content.translations.length) {
            choice.label.splice(langIndex, 1)
          } else {
            console.error('Translations index mismatch')
            return false
          }
        }
      }
    }
    return content
  }

  changeDefaultLanguage(index: number) {
    const langString = this.state.translations?.[index]

    const dialog = alertify.dialog('confirm')
    const opts = {
      title: t('Change default language?'),
      message: t('Are you sure you would like to set ##lang## as the default language for this form?').replace(
        '##lang##',
        escapeHtml(String(langString)),
      ),
      labels: { ok: t('Confirm'), cancel: t('Cancel') },
      onok: () => {
        const content = cloneDeep(this.state.asset.content)
        if (content?.settings) {
          content.settings.default_language = langString
          this.updateAsset(content)
        }
        dialog.destroy()
      },
      oncancel: dialog.destroy,
    }
    dialog.set(opts).show()
  }

  updateAsset(content: AssetContent) {
    this.setState({ isUpdatingAsset: true })

    actions.resources.updateAsset(
      this.state.asset.uid,
      { content: JSON.stringify(content) },
      // reload asset on failure
      {
        onFailed: () => {
          actions.resources.loadAsset({ id: this.state.asset.uid }, true)
          notify.error('failed to update translations')
        },
      },
    )
  }

  isEditingLanguagesLocked() {
    return (
      this.state.asset?.content && hasAssetRestriction(this.state.asset.content, LockingRestrictionName.language_edit)
    )
  }

  renderEmptyMessage() {
    return (
      <bem.FormModal m='translation-settings'>
        <bem.FormModal__item>
          <bem.FormView__cell>{t('There is nothing to translate in this form.')}</bem.FormView__cell>
        </bem.FormModal__item>
      </bem.FormModal>
    )
  }

  renderUndefinedDefaultSettings() {
    return (
      <bem.FormModal m='translation-settings'>
        <bem.FormModal__item>
          <bem.FormView__cell m='translation-note'>
            <p>{t('Here you can add more languages to your project, and translate the strings in each of them.')}</p>
            <p>
              {t('For the language code field, we suggest using the')}
              <a
                target='_blank'
                href='https://www.iana.org/assignments/language-subtag-registry/language-subtag-registry'
              >
                {' ' + t('official language code') + ' '}
              </a>
              {t('(e.g. "English (en)" or "Rohingya (rhg)").')}

              {envStore.isReady && envStore.data.support_url && (
                <a target='_blank' href={envStore.data.support_url + LANGUAGE_SUPPORT_URL}>
                  {' ' + t('Read more.')}
                </a>
              )}
            </p>
          </bem.FormView__cell>
          <bem.FormView__cell m='translation'>
            <p>
              <strong>{t('Please name your default language before adding languages and translations.')}</strong>
            </p>
          </bem.FormView__cell>
          <bem.FormView__cell m='update-language-form'>
            <LanguageForm
              isPending={this.state.isUpdatingAsset}
              onLanguageChange={this.onLanguageChange.bind(this)}
              existingLanguages={this.getAllLanguages()}
              isDefault
            />
          </bem.FormView__cell>
        </bem.FormModal__item>
      </bem.FormModal>
    )
  }

  renderTranslationsSettings(translations: Array<string | null>) {
    return (
      <bem.FormModal m='translation-settings'>
        <bem.FormModal__item>
          <bem.FormView__cell m='label'>{t('Current languages')}</bem.FormView__cell>
          {translations[0] === null && (
            <InlineMessage
              type='warning'
              icon='alert'
              message={t(
                'You have named translations in your form but the default translation is unnamed. Please specifiy a default translation or make an existing one default.',
              )}
            />
          )}
          {translations.map((l, i) => (
            <React.Fragment key={`lang-${i}`}>
              <bem.FormView__cell m='translation'>
                <bem.FormView__cell m='translation-name'>
                  {l}

                  {i === 0 && <bem.FormView__label m='default-language'>{t('default')}</bem.FormView__label>}

                  {i !== 0 && (
                    <ActionIcon
                      variant='transparent'
                      size='md'
                      onClick={() => {
                        this.changeDefaultLanguage(i)
                      }}
                      disabled={this.state.isUpdatingAsset || !this.canEditLanguages()}
                      tooltip={t('Make default')}
                      icon={IconWorldStar}
                    />
                  )}
                </bem.FormView__cell>

                <bem.FormView__cell m='translation-actions'>
                  <ActionIcon
                    variant='light'
                    size='md'
                    onClick={() => {
                      this.toggleRenameLanguageForm(i)
                    }}
                    disabled={this.state.isUpdatingAsset || !this.canEditLanguages()}
                    icon={this.state.renameLanguageIndex === i ? IconX : IconPencilFilled}
                    tooltip={t('Edit language')}
                  />

                  <ActionIcon
                    variant='light'
                    size='md'
                    onClick={() => {
                      this.launchTranslationTableModal(i)
                    }}
                    disabled={this.state.isUpdatingAsset}
                    icon={IconWorldCog}
                    tooltip={t('Update translations')}
                  />

                  {i !== 0 && (
                    <ActionIcon
                      variant='danger-secondary'
                      size='md'
                      onClick={() => {
                        this.deleteLanguage(i)
                      }}
                      disabled={this.state.isUpdatingAsset || !this.canEditLanguages()}
                      icon={IconTrashFilled}
                      tooltip={t('Delete language')}
                    />
                  )}
                </bem.FormView__cell>
              </bem.FormView__cell>

              {this.state.renameLanguageIndex === i && (
                <bem.FormView__cell m='update-language-form'>
                  <LanguageForm
                    isPending={this.state.isUpdatingAsset}
                    langString={l}
                    langIndex={i}
                    onLanguageChange={this.onLanguageChange.bind(this)}
                    existingLanguages={this.getAllLanguages()}
                  />
                </bem.FormView__cell>
              )}
            </React.Fragment>
          ))}
          {!this.state.showAddLanguageForm && (
            <bem.FormView__cell m='add-language'>
              <Button
                type='primary'
                size='l'
                onClick={this.showAddLanguageForm.bind(this)}
                isDisabled={!this.canAddLanguages() || !this.canEditLanguages()}
                label={t('Add language')}
              />
            </bem.FormView__cell>
          )}
          {this.state.showAddLanguageForm && (
            <bem.FormView__cell m='add-language-form'>
              <Button
                className='add-language-form-close'
                type='text'
                size='m'
                onClick={this.hideAddLanguageForm.bind(this)}
                startIcon='close'
              />

              <bem.FormView__cell m='label'>{t('Add a new language')}</bem.FormView__cell>

              <LanguageForm
                isPending={this.state.isUpdatingAsset}
                onLanguageChange={this.onLanguageChange.bind(this)}
                existingLanguages={this.getAllLanguages()}
              />
            </bem.FormView__cell>
          )}
        </bem.FormModal__item>
      </bem.FormModal>
    )
  }

  render() {
    if (!this.state.asset || !this.state.asset.content || this.state.translations === null) {
      return <LoadingSpinner />
    }

    const translations = this.state.translations
    if (!translations || translations?.length === 0) {
      return this.renderEmptyMessage()
    } else if (translations?.length === 1 && translations[0] === null) {
      // use this modal if there are only unnamed translations
      return this.renderUndefinedDefaultSettings()
    } else {
      return this.renderTranslationsSettings(translations)
    }
  }
}

export default TranslationSettings
